<script setup lang="ts">
import { useI18n } from '@/app/i18n'
import AppIcon from '@/shared/ui/AppIcon.vue'

import { useAiMode } from '../ai-mode'

import AiModeSwitch from './AiModeSwitch.vue'
import NavbarAlerts from './NavbarAlerts.vue'
import NavbarSearch from './NavbarSearch.vue'
import ThemeSwitch from './ThemeSwitch.vue'
import WorkspaceSwitcher from './WorkspaceSwitcher.vue'

/**
 * The navbar (wp/shell §4.16, redesigned 2026-10 as a floating row, no card): the menu toggle below 1200 px, the
 * workspace button, the search pill (⌘K palette) and, at the right, the AI-mode switch (beta flag and a session —
 * docs/ai-mode/wp/AI-1.md), the light/dark switch and the alerts bell. Profile, role switch, help and logout live in
 * the nav's account card at every width (below 1200 px the nav is an overlay behind the menu toggle), so the bar no
 * longer repeats them there. The row is as tall as the nav's software switcher card and aligned with it. The locale
 * switcher was never mounted in legacy (NEW-18) and is not ported. The AI layout has no nav: the `start` slot replaces
 * the menu toggle (it puts the brand there) and the `end` slot closes the row (it puts the account card there).
 */
defineProps<{ xl: boolean; lg: boolean }>()
const emit = defineEmits<{ toggleNav: [] }>()
defineSlots<{ start?: () => unknown; end?: () => unknown }>()
const { t } = useI18n()
const { available: aiModeAvailable } = useAiMode()
</script>

<template>
  <header class="app-navbar" data-testid="navbar">
    <slot name="start">
      <button
        v-if="!xl"
        type="button"
        class="navbar-icon"
        :aria-label="t('shell.navbar.action.menu')"
        @click="emit('toggleNav')"
      >
        <AppIcon name="MenuIcon" :size="21" />
      </button>
    </slot>
    <WorkspaceSwitcher :lg="lg" />
    <NavbarSearch :compact="!lg" class="app-navbar__search" />
    <!-- A wrapper: the alerts component has two roots (button and panel), so a class on it would not land. -->
    <div class="ml-auto flex items-center gap-3">
      <AiModeSwitch v-if="aiModeAvailable" :compact="!lg" />
      <ThemeSwitch />
      <NavbarAlerts />
      <slot name="end" />
    </div>
  </header>
</template>

<style scoped>
.app-navbar {
  display: flex;
  gap: 0.6rem;
  align-items: center;
  /* The nav's switcher card: 0.5 rem under the panel's top edge (plus its border), 54 px tall. */
  height: 54px;
  margin-top: calc(0.5rem + 1px);
}
.app-navbar__search {
  flex: 0 1 auto;
  margin-left: 0.4rem;
}
.app-navbar :deep(.navbar-icon) {
  display: inline-flex;
  align-items: center;
  padding: 0;
  color: var(--p-text-color);
  background: transparent;
  border: 0;
  cursor: pointer;
}
.app-navbar :deep(.navbar-round) {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  color: var(--p-text-color);
  cursor: pointer;
  background: var(--app-fill);
  border: 0;
  border-radius: 50%;
  transition: background 0.2s ease;
}
.app-navbar :deep(.navbar-round:hover),
.app-navbar :deep(.navbar-round[aria-expanded='true']) {
  background: var(--app-fill-hover);
}
.app-navbar :deep(.navbar-round:focus-visible) {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 2px;
}
</style>

<style>
/* The navbar's panels (workspaces, alerts) are teleported to the body: their rows are styled globally; the frame is
   the shell's `.shell-popover` (theme/tailwind.css). */
.navbar-panel__heading {
  padding: 0.5rem 0.75rem 0.4rem;
  font-size: 0.786rem;
  font-weight: 600;
  color: var(--p-surface-500);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.navbar-panel__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.navbar-panel__row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0.75rem;
  font: inherit;
  color: var(--p-text-color);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: calc(var(--p-border-radius-lg) - 2px);
}
.navbar-panel__row:hover,
.navbar-panel__row:focus-visible {
  color: var(--p-text-color);
  background: var(--p-surface-100);
  outline: none;
}
.navbar-panel__row.is-current {
  background: color-mix(in srgb, var(--p-primary-500) 10%, var(--p-surface-0));
}
.navbar-panel__row-icon {
  flex: 0 0 auto;
  color: var(--p-surface-500);
}
.navbar-panel__row-text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.navbar-panel__row-title {
  overflow: hidden;
  font-size: 0.929rem;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.navbar-panel__row-hint {
  overflow: hidden;
  font-size: 0.786rem;
  color: var(--p-surface-500);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.navbar-panel__check {
  flex: 0 0 auto;
  color: var(--p-primary-500);
}
.navbar-panel__footer {
  margin-top: 0.4rem;
  padding-top: 0.4rem;
  border-top: 1px solid var(--p-content-border-color);
}
.navbar-panel__empty {
  margin: 0;
  padding: 1rem 0.75rem 0.9rem;
  font-size: 0.929rem;
  color: var(--p-surface-500);
  text-align: center;
}
.navbar-alerts__panel {
  --shell-popover-width: 340px;
}
</style>
